import { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, Buildings, Briefcase, MagnifyingGlass, Trash, PencilSimple, Eye, Upload, X, FileText, Download, CircleNotch, Plus, FilePdf, FileImage, File, ArrowUp, ArrowDown, ArrowLineUp, ArrowLineDown } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { ClientContextBanner, ClientContextIndicator } from '../../components/accountant/ClientContextBanner';
import { toast } from 'sonner';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';
import { DraftRestoreBanner, DraftSavedIndicator } from '../../components/DraftBanner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HRPage() {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const { formatCurrency } = useCurrency();
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showDesignationModal, setShowDesignationModal] = useState(false);
  const [showEmployeeDetail, setShowEmployeeDetail] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Edit states for Departments & Designations
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editingDesignation, setEditingDesignation] = useState(null);
  
  // Inline creation toggles for Employee form
  const [showNewDepartment, setShowNewDepartment] = useState(false);
  const [showNewDesignation, setShowNewDesignation] = useState(false);
  const [inlineDeptForm, setInlineDeptForm] = useState({ name: '', code: '', description: '' });
  const [inlineDesigForm, setInlineDesigForm] = useState({ title: '', level: 1, description: '' });
  
  // Form states
  const [employeeForm, setEmployeeForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    department_id: '', designation_id: '', joining_date: '',
    basic_salary: 0, housing_allowance: 0, transport_allowance: 0, other_allowances: 0,
    overtime_rate: 0, annual_leave_days: 30, leave_balance: 0,
    visa_number: '', visa_expiry: '', passport_number: '', passport_expiry: '',
    national_id: '', insurance_policy: '', insurance_expiry: '',
    bank_name: '', bank_account: '', iban: ''
  });
  const [deptForm, setDeptForm] = useState({ name: '', code: '', description: '' });
  const [desigForm, setDesigForm] = useState({ title: '', department_id: '', level: 1, description: '' });

  // Draft auto-save for new employee creation (not edits)
  const employeeDraft = useDraftAutosave({
    key: 'hr-employee',
    data: employeeForm,
    enabled: showEmployeeModal && !isEditing,
    userId: user?.id,
  });
  
  // Document upload - enhanced for multi-file
  const [uploadForm, setUploadForm] = useState({ document_type: 'passport', document_name: '', expiry_date: '' });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);
  const [docPreviewUrl, setDocPreviewUrl] = useState('');
  const [docPreviewName, setDocPreviewName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [empRes, deptRes, desigRes, statsRes] = await Promise.all([
        fetch(`${API}/api/hr/employees`, { headers }),
        fetch(`${API}/api/hr/departments`, { headers }),
        fetch(`${API}/api/hr/designations`, { headers }),
        fetch(`${API}/api/hr/stats`, { headers })
      ]);
      
      if (empRes.ok) setEmployees(await empRes.json());
      if (deptRes.ok) setDepartments(await deptRes.json());
      if (desigRes.ok) setDesignations(await desigRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      toast.error('Failed to load HR data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/hr/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...employeeForm,
          basic_salary: parseFloat(employeeForm.basic_salary) || 0,
          housing_allowance: parseFloat(employeeForm.housing_allowance) || 0,
          transport_allowance: parseFloat(employeeForm.transport_allowance) || 0,
          other_allowances: parseFloat(employeeForm.other_allowances) || 0
        })
      });
      
      if (res.ok) {
        toast.success('Employee created successfully');
        setShowEmployeeModal(false);
        employeeDraft.clear();
        resetEmployeeForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create employee');
      }
    } catch (err) {
      toast.error('Failed to create employee');
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/hr/employees/${selectedEmployee.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...employeeForm,
          basic_salary: parseFloat(employeeForm.basic_salary) || 0,
          housing_allowance: parseFloat(employeeForm.housing_allowance) || 0,
          transport_allowance: parseFloat(employeeForm.transport_allowance) || 0,
          other_allowances: parseFloat(employeeForm.other_allowances) || 0
        })
      });
      
      if (res.ok) {
        toast.success('Employee updated successfully');
        setShowEmployeeModal(false);
        setIsEditing(false);
        resetEmployeeForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update employee');
      }
    } catch (err) {
      toast.error('Failed to update employee');
    }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Are you sure you want to terminate this employee?\n\nThe record stays in the system (status = Terminated) so payroll history and audit trails remain intact.')) return;
    try {
      const res = await fetch(`${API}/api/hr/employees/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Employee terminated');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to terminate employee');
    }
  };

  const handleHardDeleteEmployee = async (emp) => {
    const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.id;
    const warn = `PERMANENT DELETE — ${name}\n\nThis will permanently remove the employee record and all uploaded documents from the database.\n\nThis cannot be undone. Linked payroll records (if any) will block the delete.\n\nType "DELETE" to confirm:`;
    const confirmText = window.prompt(warn);
    if ((confirmText || '').trim().toUpperCase() !== 'DELETE') {
      if (confirmText !== null) toast.info('Permanent delete cancelled.');
      return;
    }
    try {
      const res = await fetch(`${API}/api/hr/employees/${emp.id}?force=true`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success('Employee permanently deleted');
        fetchData();
      } else {
        toast.error(data.detail || data.message || 'Failed to permanently delete employee');
      }
    } catch (err) {
      toast.error('Failed to permanently delete employee');
    }
  };

  const reorderEmployee = async (empId, direction) => {
    try {
      const res = await fetch(`${API}/api/hr/employees/${empId}/reorder?direction=${direction}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        fetchData();
      } else {
        toast.error(data.detail || data.message || 'Failed to reorder');
      }
    } catch (err) {
      toast.error('Failed to reorder employee');
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/hr/departments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(deptForm)
      });
      
      if (res.ok) {
        toast.success('Department created successfully');
        setShowDepartmentModal(false);
        setDeptForm({ name: '', code: '', description: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create department');
      }
    } catch (err) {
      toast.error('Failed to create department');
    }
  };

  const handleUpdateDepartment = async (e) => {
    e.preventDefault();
    if (!editingDepartment) return;
    try {
      const res = await fetch(`${API}/api/hr/departments/${editingDepartment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(deptForm)
      });
      
      if (res.ok) {
        toast.success('Department updated successfully');
        setShowDepartmentModal(false);
        setEditingDepartment(null);
        setDeptForm({ name: '', code: '', description: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update department');
      }
    } catch (err) {
      toast.error('Failed to update department');
    }
  };

  const handleEditDepartment = (dept) => {
    setEditingDepartment(dept);
    setDeptForm({
      name: dept.name || '',
      code: dept.code || '',
      description: dept.description || ''
    });
    setShowDepartmentModal(true);
  };

  const handleDeleteDepartment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    try {
      const res = await fetch(`${API}/api/hr/departments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Department deleted');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete department');
      }
    } catch (err) {
      toast.error('Failed to delete department');
    }
  };

  const handleCreateDesignation = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/hr/designations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(desigForm)
      });
      
      if (res.ok) {
        toast.success('Designation created successfully');
        setShowDesignationModal(false);
        setDesigForm({ title: '', department_id: '', level: 1, description: '' });
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to create designation');
    }
  };

  const handleUpdateDesignation = async (e) => {
    e.preventDefault();
    if (!editingDesignation) return;
    try {
      const res = await fetch(`${API}/api/hr/designations/${editingDesignation.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(desigForm)
      });
      
      if (res.ok) {
        toast.success('Designation updated successfully');
        setShowDesignationModal(false);
        setEditingDesignation(null);
        setDesigForm({ title: '', department_id: '', level: 1, description: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update designation');
      }
    } catch (err) {
      toast.error('Failed to update designation');
    }
  };

  const handleEditDesignation = (desig) => {
    setEditingDesignation(desig);
    setDesigForm({
      title: desig.title || '',
      department_id: desig.department_id || '',
      level: desig.level || 1,
      description: desig.description || ''
    });
    setShowDesignationModal(true);
  };

  const handleDeleteDesignation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this designation?')) return;
    try {
      const res = await fetch(`${API}/api/hr/designations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Designation deleted');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete designation');
      }
    } catch (err) {
      toast.error('Failed to delete designation');
    }
  };

  // Inline creation handlers for Employee form
  const handleInlineCreateDepartment = async () => {
    if (!inlineDeptForm.name) {
      toast.error('Department name is required');
      return;
    }
    try {
      const res = await fetch(`${API}/api/hr/departments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(inlineDeptForm)
      });
      
      if (res.ok) {
        const newDept = await res.json();
        toast.success('Department created');
        setShowNewDepartment(false);
        setInlineDeptForm({ name: '', code: '', description: '' });
        // Refresh departments and select the new one
        const deptRes = await fetch(`${API}/api/hr/departments`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (deptRes.ok) {
          const depts = await deptRes.json();
          setDepartments(depts);
          // Auto-select the newly created department
          setEmployeeForm(prev => ({ ...prev, department_id: newDept.id }));
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create department');
      }
    } catch (err) {
      toast.error('Failed to create department');
    }
  };

  const handleInlineCreateDesignation = async () => {
    if (!inlineDesigForm.title) {
      toast.error('Designation title is required');
      return;
    }
    try {
      // Use selected department if available
      const payload = {
        ...inlineDesigForm,
        department_id: employeeForm.department_id || null
      };
      const res = await fetch(`${API}/api/hr/designations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const newDesig = await res.json();
        toast.success('Designation created');
        setShowNewDesignation(false);
        setInlineDesigForm({ title: '', level: 1, description: '' });
        // Refresh designations and select the new one
        const desigRes = await fetch(`${API}/api/hr/designations`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (desigRes.ok) {
          const desigs = await desigRes.json();
          setDesignations(desigs);
          // Auto-select the newly created designation
          setEmployeeForm(prev => ({ ...prev, designation_id: newDesig.id }));
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create designation');
      }
    } catch (err) {
      toast.error('Failed to create designation');
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0 || !selectedEmployee) return;
    
    setUploadingDocs(true);
    let successCount = 0;
    
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', uploadForm.document_type);
      formData.append('document_name', uploadForm.document_name || file.name);
      if (uploadForm.expiry_date) formData.append('expiry_date', uploadForm.expiry_date);
      
      try {
        const res = await fetch(`${API}/api/hr/employees/${selectedEmployee.id}/documents`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) successCount++;
      } catch (err) {
        console.error('Upload failed for:', file.name);
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} document(s) uploaded successfully`);
      setSelectedFiles([]);
      setUploadForm({ document_type: 'passport', document_name: '', expiry_date: '' });
      // Refresh employee data
      const empRes = await fetch(`${API}/api/hr/employees/${selectedEmployee.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (empRes.ok) setSelectedEmployee(await empRes.json());
    } else {
      toast.error('Failed to upload documents');
    }
    setUploadingDocs(false);
  };

  const handleDownloadDocument = async (docId, fileName) => {
    try {
      const res = await fetch(`${API}/api/hr/employees/${selectedEmployee.id}/documents/${docId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Downloaded: ${fileName}`);
      }
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  const handlePreviewDocument = async (docId, fileName) => {
    try {
      const res = await fetch(`${API}/api/hr/employees/${selectedEmployee.id}/documents/${docId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        setDocPreviewUrl(url);
        setDocPreviewName(fileName);
        setDocPreviewOpen(true);
      }
    } catch (err) {
      toast.error('Failed to preview document');
    }
  };

  const closeDocPreview = () => {
    if (docPreviewUrl) window.URL.revokeObjectURL(docPreviewUrl);
    setDocPreviewOpen(false);
    setDocPreviewUrl('');
    setDocPreviewName('');
  };

  const handleAddFilesToQueue = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeFileFromQueue = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const res = await fetch(`${API}/api/hr/employees/${selectedEmployee.id}/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Document deleted');
        const empRes = await fetch(`${API}/api/hr/employees/${selectedEmployee.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (empRes.ok) setSelectedEmployee(await empRes.json());
      }
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      first_name: '', last_name: '', email: '', phone: '',
      department_id: '', designation_id: '', joining_date: '',
      basic_salary: 0, housing_allowance: 0, transport_allowance: 0, other_allowances: 0,
      visa_number: '', visa_expiry: '', passport_number: '', passport_expiry: '',
      national_id: '', insurance_policy: '', insurance_expiry: '',
      bank_name: '', bank_account: '', iban: ''
    });
    setSelectedEmployee(null);
    setIsEditing(false);
    // Reset inline creation forms
    setShowNewDepartment(false);
    setShowNewDesignation(false);
    setInlineDeptForm({ name: '', code: '', description: '' });
    setInlineDesigForm({ title: '', level: 1, description: '' });
  };

  const openEditEmployee = (emp) => {
    setSelectedEmployee(emp);
    setEmployeeForm({
      first_name: emp.first_name, last_name: emp.last_name, email: emp.email, phone: emp.phone || '',
      department_id: emp.department_id || '', designation_id: emp.designation_id || '', joining_date: emp.joining_date,
      basic_salary: emp.basic_salary, housing_allowance: emp.housing_allowance, transport_allowance: emp.transport_allowance, other_allowances: emp.other_allowances,
      visa_number: emp.visa_number || '', visa_expiry: emp.visa_expiry || '', passport_number: emp.passport_number || '', passport_expiry: emp.passport_expiry || '',
      national_id: emp.national_id || '', insurance_policy: emp.insurance_policy || '', insurance_expiry: emp.insurance_expiry || '',
      bank_name: emp.bank_name || '', bank_account: emp.bank_account || '', iban: emp.iban || ''
    });
    setIsEditing(true);
    setShowEmployeeModal(true);
  };

  const openEmployeeDetail = async (emp) => {
    try {
      const res = await fetch(`${API}/api/hr/employees/${emp.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSelectedEmployee(await res.json());
        setShowEmployeeDetail(true);
      }
    } catch (err) {
      toast.error('Failed to load employee details');
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = search === '' || 
      emp.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      emp.email?.toLowerCase().includes(search.toLowerCase()) ||
      emp.employee_id?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || emp.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/10 text-emerald-600',
      on_leave: 'bg-amber-500/10 text-amber-600',
      inactive: 'bg-gray-500/10 text-gray-600',
      terminated: 'bg-red-500/10 text-red-600'
    };
    return <Badge className={styles[status] || styles.inactive}>{status?.replace('_', ' ')}</Badge>;
  };

  const statCards = [
    { label: 'Total Employees', value: stats?.total_employees || 0, icon: Users, color: 'bg-emerald-500/10 text-emerald-600' },
    { label: 'Active Employees', value: stats?.active_employees || 0, icon: Users, color: 'bg-blue-500/10 text-blue-600' },
    { label: 'On Leave', value: stats?.on_leave || 0, icon: Users, color: 'bg-amber-500/10 text-amber-600' },
    { label: 'Departments', value: stats?.departments || 0, icon: Buildings, color: 'bg-purple-500/10 text-purple-600' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="hr-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="hr-page">
      {/* Client Context Banner for Accountant Mode */}
      <ClientContextBanner />
      
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Human Resources
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Manage employees, departments, and organizational structure
          </p>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card-tactile rounded-lg p-4 sm:p-6"
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${stat.color}`}>
                <stat.icon size={20} weight="duotone" />
              </div>
              <div>
                <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
                  {stat.label}
                </span>
                <p className="text-xl sm:text-2xl font-heading font-semibold mt-0.5">
                  {stat.value}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="employees" data-testid="tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">Departments</TabsTrigger>
          <TabsTrigger value="designations" data-testid="tab-designations">Designations</TabsTrigger>
        </TabsList>

        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-2 flex-1">
              <div className="relative flex-1 max-w-sm">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input 
                  placeholder="Search employees..." 
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="employee-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { resetEmployeeForm(); setShowEmployeeModal(true); }} data-testid="add-employee-btn">
              <UserPlus size={18} className="mr-2" />
              Add Employee
            </Button>
          </div>

          <div className="card-tactile rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead className="hidden md:table-cell">Department</TableHead>
                  <TableHead className="hidden lg:table-cell">Designation</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No employees found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((emp, idx) => (
                    <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{emp.full_name}</p>
                          <p className="text-sm text-muted-foreground">{emp.employee_id}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{emp.department_name || '-'}</TableCell>
                      <TableCell className="hidden lg:table-cell">{emp.designation_title || '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell">{getStatusBadge(emp.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-1">
                          {/* Quick reorder arrows — inline so user can move without opening menu */}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={idx === 0}
                            onClick={() => reorderEmployee(emp.id, 'up')}
                            title="Move up"
                            data-testid={`employee-${emp.id}-move-up`}
                          >
                            <ArrowUp size={14} />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={idx === filteredEmployees.length - 1}
                            onClick={() => reorderEmployee(emp.id, 'down')}
                            title="Move down"
                            data-testid={`employee-${emp.id}-move-down`}
                          >
                            <ArrowDown size={14} />
                          </Button>
                          <RowActionsMenu
                            testid={`employee-${emp.id}`}
                            actions={[
                              { id: 'view', label: 'View Details', icon: Eye, onClick: () => openEmployeeDetail(emp) },
                              { id: 'edit', label: 'Edit', icon: PencilSimple, onClick: () => openEditEmployee(emp) },
                              'separator',
                              { id: 'move-top', label: 'Move to Top', icon: ArrowLineUp, onClick: () => reorderEmployee(emp.id, 'top') },
                              { id: 'move-bottom', label: 'Move to Bottom', icon: ArrowLineDown, onClick: () => reorderEmployee(emp.id, 'bottom') },
                              'separator',
                              { id: 'delete', label: 'Terminate (soft delete)', icon: Trash, onClick: () => handleDeleteEmployee(emp.id), destructive: true },
                              ...(isAdmin ? [{ id: 'hard-delete', label: 'Permanently Delete', icon: Trash, onClick: () => handleHardDeleteEmployee(emp), destructive: true }] : []),
                            ]}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Departments Tab */}
        <TabsContent value="departments" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => {
              setEditingDepartment(null);
              setDeptForm({ name: '', code: '', description: '' });
              setShowDepartmentModal(true);
            }} data-testid="add-department-btn">
              <Buildings size={18} className="mr-2" />
              Add Department
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {departments.length === 0 ? (
              <div className="col-span-full card-tactile rounded-lg p-8 text-center text-muted-foreground">
                No departments yet. Create your first department.
              </div>
            ) : (
              departments.map((dept) => (
                <motion.div
                  key={dept.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="card-tactile rounded-lg p-4"
                  data-testid={`department-card-${dept.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{dept.name}</h3>
                      <p className="text-sm text-muted-foreground">Code: {dept.code}</p>
                      {dept.description && <p className="text-sm mt-2">{dept.description}</p>}
                    </div>
                    <div className="flex gap-1">
                      <RowActionsMenu
                        testid={`dept-${dept.id}`}
                        actions={[
                          { id: 'edit', label: 'Edit', icon: PencilSimple, onClick: () => handleEditDepartment(dept) },
                          { id: 'delete', label: 'Delete', icon: Trash, onClick: () => handleDeleteDepartment(dept.id), destructive: true },
                        ]}
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm"><strong>{dept.employee_count}</strong> employees</p>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Designations Tab */}
        <TabsContent value="designations" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => {
              setEditingDesignation(null);
              setDesigForm({ title: '', department_id: '', level: 1, description: '' });
              setShowDesignationModal(true);
            }} data-testid="add-designation-btn">
              <Briefcase size={18} className="mr-2" />
              Add Designation
            </Button>
          </div>

          <div className="card-tactile rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead className="hidden sm:table-cell">Description</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {designations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No designations yet
                    </TableCell>
                  </TableRow>
                ) : (
                  designations.map((desig) => (
                    <TableRow key={desig.id} data-testid={`designation-row-${desig.id}`}>
                      <TableCell className="font-medium">{desig.title}</TableCell>
                      <TableCell>Level {desig.level}</TableCell>
                      <TableCell className="hidden sm:table-cell">{desig.description || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <RowActionsMenu
                            testid={`desig-${desig.id}`}
                            actions={[
                              { id: 'edit', label: 'Edit', icon: PencilSimple, onClick: () => handleEditDesignation(desig) },
                              { id: 'delete', label: 'Delete', icon: Trash, onClick: () => handleDeleteDesignation(desig.id), destructive: true },
                            ]}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Employee Modal */}
      <Dialog open={showEmployeeModal} onOpenChange={(open) => { setShowEmployeeModal(open); if (!open) resetEmployeeForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>Fill in the employee details below</DialogDescription>
          </DialogHeader>
          {/* Show which company this employee is being added to */}
          <ClientContextIndicator label={isEditing ? "Editing employee in" : "Adding employee to"} />
          <form onSubmit={isEditing ? handleUpdateEmployee : handleCreateEmployee} className="space-y-6">
            {!isEditing && (
              <DraftRestoreBanner
                visible={employeeDraft.hasDraft}
                savedAt={employeeDraft.draftSavedAt}
                label="employee"
                onRestore={() => { const r = employeeDraft.restore(); if (r) setEmployeeForm(r); }}
                onDiscard={employeeDraft.discard}
              />
            )}
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Basic Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>First Name *</Label><Input value={employeeForm.first_name} onChange={(e) => setEmployeeForm({...employeeForm, first_name: e.target.value})} required /></div>
                <div><Label>Last Name *</Label><Input value={employeeForm.last_name} onChange={(e) => setEmployeeForm({...employeeForm, last_name: e.target.value})} required /></div>
                <div><Label>Email *</Label><Input type="email" value={employeeForm.email} onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})} required /></div>
                <div><Label>Phone</Label><Input value={employeeForm.phone} onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})} /></div>
                
                {/* Department with inline creation */}
                <div>
                  <Label>Department</Label>
                  {!showNewDepartment ? (
                    <div className="flex gap-2">
                      <Select value={employeeForm.department_id} onValueChange={(v) => setEmployeeForm({...employeeForm, department_id: v})}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Select department" /></SelectTrigger>
                        <SelectContent>
                          {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={() => setShowNewDepartment(true)} title="Create new department" data-testid="add-dept-inline-btn">
                        <Buildings size={18} />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">New Department</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewDepartment(false)}>Cancel</Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Name *" value={inlineDeptForm.name} onChange={(e) => setInlineDeptForm(p => ({...p, name: e.target.value}))} data-testid="inline-dept-name" />
                        <Input placeholder="Code (e.g. HR)" value={inlineDeptForm.code} onChange={(e) => setInlineDeptForm(p => ({...p, code: e.target.value}))} data-testid="inline-dept-code" />
                      </div>
                      <Button type="button" size="sm" onClick={handleInlineCreateDepartment} data-testid="save-inline-dept-btn">
                        <Buildings size={14} className="mr-1" /> Create & Select
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Designation with inline creation */}
                <div>
                  <Label>Designation</Label>
                  {!showNewDesignation ? (
                    <div className="flex gap-2">
                      <Select value={employeeForm.designation_id} onValueChange={(v) => setEmployeeForm({...employeeForm, designation_id: v})}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Select designation" /></SelectTrigger>
                        <SelectContent>
                          {designations.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="outline" size="icon" onClick={() => setShowNewDesignation(true)} title="Create new designation" data-testid="add-desig-inline-btn">
                        <Briefcase size={18} />
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">New Designation</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewDesignation(false)}>Cancel</Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Title *" value={inlineDesigForm.title} onChange={(e) => setInlineDesigForm(p => ({...p, title: e.target.value}))} data-testid="inline-desig-title" />
                        <Input type="number" placeholder="Level" min="1" value={inlineDesigForm.level} onChange={(e) => setInlineDesigForm(p => ({...p, level: parseInt(e.target.value) || 1}))} data-testid="inline-desig-level" />
                      </div>
                      <Button type="button" size="sm" onClick={handleInlineCreateDesignation} data-testid="save-inline-desig-btn">
                        <Briefcase size={14} className="mr-1" /> Create & Select
                      </Button>
                    </div>
                  )}
                </div>
                
                <div><Label>Joining Date *</Label><Input type="date" value={employeeForm.joining_date} onChange={(e) => setEmployeeForm({...employeeForm, joining_date: e.target.value})} required /></div>
              </div>
            </div>

            {/* Salary Info */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Salary Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Basic Salary</Label><Input type="number" value={employeeForm.basic_salary} onChange={(e) => setEmployeeForm({...employeeForm, basic_salary: e.target.value})} /></div>
                <div><Label>Housing Allowance</Label><Input type="number" value={employeeForm.housing_allowance} onChange={(e) => setEmployeeForm({...employeeForm, housing_allowance: e.target.value})} /></div>
                <div><Label>Transport Allowance</Label><Input type="number" value={employeeForm.transport_allowance} onChange={(e) => setEmployeeForm({...employeeForm, transport_allowance: e.target.value})} /></div>
                <div><Label>Other Allowances</Label><Input type="number" value={employeeForm.other_allowances} onChange={(e) => setEmployeeForm({...employeeForm, other_allowances: e.target.value})} /></div>
                <div><Label>Overtime Rate (Hourly)</Label><Input type="number" step="0.01" value={employeeForm.overtime_rate} onChange={(e) => setEmployeeForm({...employeeForm, overtime_rate: e.target.value})} placeholder="Leave 0 for auto-calc" /></div>
                <div><Label>Annual Leave Days</Label><Input type="number" value={employeeForm.annual_leave_days} onChange={(e) => setEmployeeForm({...employeeForm, annual_leave_days: e.target.value})} /></div>
                <div><Label>Current Leave Balance</Label><Input type="number" step="0.5" value={employeeForm.leave_balance} onChange={(e) => setEmployeeForm({...employeeForm, leave_balance: e.target.value})} /></div>
              </div>
            </div>

            {/* Compliance Info */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Compliance Documents</h4>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Passport Number</Label><Input value={employeeForm.passport_number} onChange={(e) => setEmployeeForm({...employeeForm, passport_number: e.target.value})} /></div>
                <div><Label>Passport Expiry</Label><Input type="date" value={employeeForm.passport_expiry} onChange={(e) => setEmployeeForm({...employeeForm, passport_expiry: e.target.value})} /></div>
                <div><Label>Visa Number</Label><Input value={employeeForm.visa_number} onChange={(e) => setEmployeeForm({...employeeForm, visa_number: e.target.value})} /></div>
                <div><Label>Visa Expiry</Label><Input type="date" value={employeeForm.visa_expiry} onChange={(e) => setEmployeeForm({...employeeForm, visa_expiry: e.target.value})} /></div>
                <div><Label>National ID</Label><Input value={employeeForm.national_id} onChange={(e) => setEmployeeForm({...employeeForm, national_id: e.target.value})} /></div>
              </div>
            </div>

            {/* Bank Info */}
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Bank Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Bank Name</Label><Input value={employeeForm.bank_name} onChange={(e) => setEmployeeForm({...employeeForm, bank_name: e.target.value})} /></div>
                <div><Label>Account Number</Label><Input value={employeeForm.bank_account} onChange={(e) => setEmployeeForm({...employeeForm, bank_account: e.target.value})} /></div>
                <div className="col-span-2"><Label>IBAN</Label><Input value={employeeForm.iban} onChange={(e) => setEmployeeForm({...employeeForm, iban: e.target.value})} /></div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              {!isEditing && <div className="mr-auto"><DraftSavedIndicator savedAt={employeeDraft.lastAutoSavedAt} /></div>}
              <Button type="button" variant="outline" onClick={() => { setShowEmployeeModal(false); resetEmployeeForm(); }}>Cancel</Button>
              <Button type="submit" data-testid="save-employee-btn">{isEditing ? 'Update' : 'Create'} Employee</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Employee Detail Modal */}
      <Dialog open={showEmployeeDetail} onOpenChange={setShowEmployeeDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEmployee?.full_name}</DialogTitle>
            <DialogDescription>{selectedEmployee?.employee_id} • {selectedEmployee?.email}</DialogDescription>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="space-y-6">
              {/* Info Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Department</p>
                  <p className="font-medium">{selectedEmployee.department_name || 'Not assigned'}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Designation</p>
                  <p className="font-medium">{selectedEmployee.designation_title || 'Not assigned'}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Status</p>
                  {getStatusBadge(selectedEmployee.status)}
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Total Salary</p>
                  <p className="font-medium">{formatCurrency(selectedEmployee.total_salary)}</p>
                </div>
              </div>

              {/* Documents Section - Enhanced with View/Download and Multi-Upload */}
              <div className="space-y-4">
                <h4 className="font-medium">Documents</h4>
                
                {/* Document List with Thumbnails and View/Download */}
                {selectedEmployee.documents?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedEmployee.documents.map((doc) => {
                      const ext = doc.file_name?.split('.').pop()?.toLowerCase();
                      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
                      const isPdf = ext === 'pdf';
                      const canPreview = isImage || isPdf;
                      const DocIcon = isPdf ? FilePdf : isImage ? FileImage : File;
                      const hasThumbnail = doc.thumbnail && !doc.thumbnail.includes('svg+xml');
                      
                      return (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`hr-doc-${doc.id}`}>
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {/* Thumbnail or Icon */}
                            {hasThumbnail ? (
                              <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex-shrink-0 border">
                                <img 
                                  src={doc.thumbnail} 
                                  alt={doc.document_name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                <DocIcon size={20} className="text-primary" weight="duotone" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{doc.document_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {doc.document_type} {doc.expiry_date && `• Expires: ${doc.expiry_date}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {canPreview && (
                              <Button variant="ghost" size="icon" onClick={() => handlePreviewDocument(doc.id, doc.file_name)} title="Preview">
                                <Eye size={16} />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => handleDownloadDocument(doc.id, doc.file_name)} title="Download">
                              <Download size={16} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteDocument(doc.id)} title="Delete">
                              <Trash size={14} className="text-destructive" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No documents uploaded</p>
                )}

                {/* Enhanced Upload Form with Multi-File Queue */}
                <form onSubmit={handleDocumentUpload} className="space-y-4 p-4 border rounded-lg">
                  <h5 className="font-medium text-sm">Upload Documents</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Document Type</Label>
                      <Select value={uploadForm.document_type} onValueChange={(v) => setUploadForm({...uploadForm, document_type: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="passport">Passport</SelectItem>
                          <SelectItem value="visa">Visa</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="id_card">ID Card</SelectItem>
                          <SelectItem value="certificate">Certificate</SelectItem>
                          <SelectItem value="emirates_id">Emirates ID</SelectItem>
                          <SelectItem value="license">License</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Document Name (optional)</Label>
                      <Input value={uploadForm.document_name} onChange={(e) => setUploadForm({...uploadForm, document_name: e.target.value})} placeholder="Auto-uses filename if empty" />
                    </div>
                    <div>
                      <Label>Expiry Date (optional)</Label>
                      <Input type="date" value={uploadForm.expiry_date} onChange={(e) => setUploadForm({...uploadForm, expiry_date: e.target.value})} />
                    </div>
                    <div>
                      <Label>Add Files (can add multiple)</Label>
                      <Input type="file" onChange={handleAddFilesToQueue} multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp" data-testid="hr-doc-file-input" />
                    </div>
                  </div>
                  
                  {/* File Queue */}
                  {selectedFiles.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-muted-foreground">Files to upload ({selectedFiles.length}):</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="flex items-center gap-1 bg-muted/50 px-2 py-1 rounded text-xs">
                            <span className="truncate max-w-[120px]">{file.name}</span>
                            <button type="button" onClick={() => removeFileFromQueue(index)} className="text-muted-foreground hover:text-destructive">
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Button type="submit" size="sm" disabled={selectedFiles.length === 0 || uploadingDocs} data-testid="upload-hr-docs-btn">
                    {uploadingDocs ? (
                      <>
                        <CircleNotch size={16} className="mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Upload {selectedFiles.length > 1 ? `${selectedFiles.length} Files` : 'Document'}
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Department Modal */}
      <Dialog open={showDepartmentModal} onOpenChange={(open) => {
        setShowDepartmentModal(open);
        if (!open) {
          setEditingDepartment(null);
          setDeptForm({ name: '', code: '', description: '' });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDepartment ? 'Edit Department' : 'Add Department'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingDepartment ? handleUpdateDepartment : handleCreateDepartment} className="space-y-4">
            <div><Label>Name *</Label><Input value={deptForm.name} onChange={(e) => setDeptForm({...deptForm, name: e.target.value})} required data-testid="dept-name-input" /></div>
            <div><Label>Code *</Label><Input value={deptForm.code} onChange={(e) => setDeptForm({...deptForm, code: e.target.value})} placeholder="e.g., HR, FIN, IT" required data-testid="dept-code-input" /></div>
            <div><Label>Description</Label><Input value={deptForm.description} onChange={(e) => setDeptForm({...deptForm, description: e.target.value})} data-testid="dept-desc-input" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowDepartmentModal(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-department-btn">{editingDepartment ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Designation Modal */}
      <Dialog open={showDesignationModal} onOpenChange={(open) => {
        setShowDesignationModal(open);
        if (!open) {
          setEditingDesignation(null);
          setDesigForm({ title: '', department_id: '', level: 1, description: '' });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDesignation ? 'Edit Designation' : 'Add Designation'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={editingDesignation ? handleUpdateDesignation : handleCreateDesignation} className="space-y-4">
            <div><Label>Title *</Label><Input value={desigForm.title} onChange={(e) => setDesigForm({...desigForm, title: e.target.value})} required data-testid="desig-title-input" /></div>
            <div>
              <Label>Department</Label>
              <Select value={desigForm.department_id || ""} onValueChange={(v) => setDesigForm({...desigForm, department_id: v})}>
                <SelectTrigger data-testid="desig-dept-select">
                  <SelectValue placeholder="Select Department (Optional)" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Level</Label><Input type="number" value={desigForm.level} onChange={(e) => setDesigForm({...desigForm, level: parseInt(e.target.value) || 1})} min={1} max={10} data-testid="desig-level-input" /></div>
            <div><Label>Description</Label><Input value={desigForm.description} onChange={(e) => setDesigForm({...desigForm, description: e.target.value})} data-testid="desig-desc-input" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowDesignationModal(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-designation-btn">{editingDesignation ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={docPreviewOpen} onOpenChange={closeDocPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={20} />
              {docPreviewName}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto bg-muted/30 rounded-lg min-h-[400px]">
            {docPreviewName?.toLowerCase().endsWith('.pdf') ? (
              <iframe
                src={docPreviewUrl}
                className="w-full h-full min-h-[500px] rounded-lg"
                title={docPreviewName}
              />
            ) : (
              <div className="flex items-center justify-center p-4">
                <img
                  src={docPreviewUrl}
                  alt={docPreviewName}
                  className="max-w-full max-h-[600px] object-contain rounded-lg"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={closeDocPreview}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
